import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import SharedFormGroup from "./FormGroup";
import './PricingConfig.css';

const EditPricingModal = ({ activeModal, setActiveModal, setUpdateModal, currentConfig }) => {
  const [formData, setFormData] = useState({
    baseCharge: 0,
    currency: 'USD',
    companyUser: 0,
    readWrite: 0,
    topo: 0,
    readOnly: 0,
    foundationAnalysisAddOn: 0,
    floorLevelAddOn: 0
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef({});


  const setInputRef = useCallback((name) => (element) => {
    if (element) {
      inputRefs.current[name] = element;
    } else {
      delete inputRefs.current[name];
    }
  }, []);

  const supportedCurrencies = [
    { code: 'usd', name: 'US Dollar', symbol: '$' },
    // { code: 'eur', name: 'Euro', symbol: '€' },
    // { code: 'gbp', name: 'British Pound', symbol: '£' },
    // { code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
    // { code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
    // { code: 'jpy', name: 'Japanese Yen', symbol: '¥' },
    // { code: 'inr', name: 'Indian Rupee', symbol: '₹' }
  ];

  useEffect(() => {
    if (currentConfig && activeModal) {
      setFormData({
        baseCharge: currentConfig.baseCharge || 0,
        currency: currentConfig.currency || 'USD',
        companyUser: currentConfig.companyUser || 0,
        readWrite: currentConfig.readWrite || 0,
        topo: currentConfig.topo || 0,
        readOnly: currentConfig.readOnly || 0,
        foundationAnalysisAddOn: currentConfig.foundationAnalysisAddOn || 0,
        floorLevelAddOn: currentConfig.floorLevelAddOn || 0
      });
    }
  }, [currentConfig, activeModal]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type } = e.target;

    let selectionStart, selectionEnd;
    if (['text', 'number', 'email'].includes(type)) {
      selectionStart = e.target.selectionStart;
      selectionEnd = e.target.selectionEnd;
    }

    const newValue = name === 'currency' ? value :
      parseFloat(value) >= 0 ? parseFloat(value) || 0 : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }


    if (['text', 'number', 'email'].includes(type)) {
      setTimeout(() => {
        const inputEl = inputRefs.current[name];
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(selectionStart, selectionEnd);
        }
      }, 0);
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    // Validate base charge
    if (formData.baseCharge < 0 || formData.baseCharge > 10000) {
      newErrors.baseCharge = 'Base charge must be between 0 and 10,000';
    }

    // Validate user type pricing
    const userTypes = ['companyUser', 'readWrite', 'topo', 'readOnly'];
    userTypes.forEach(type => {
      if (formData[type] < 0 || formData[type] > 1000) {
        const fieldName = type === 'companyUser' ? 'Company User' :
          type === 'readWrite' ? 'Read Write User' :
            type === 'topo' ? 'Topo User (seat)' : 'Read Only User';
        newErrors[type] = `${fieldName} price must be between 0 and 1,000`;
      }
    });

    // Validate add-on pricing
    const addOns = ['foundationAnalysisAddOn', 'floorLevelAddOn'];
    addOns.forEach(addOn => {
      if (formData[addOn] < 0 || formData[addOn] > 5000) {
        const fieldName = addOn === 'foundationAnalysisAddOn' ? 'Foundation Analysis' : 'Floor Level';
        newErrors[addOn] = `${fieldName} add-on price must be between 0 and 5,000`;
      }
    });

    // Validate currency
    if (!supportedCurrencies.find(curr => curr.code === formData.currency)) {
      newErrors.currency = 'Please select a valid currency';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    setIsLoading(true);

    try {
      const myurl = `${process.env.REACT_APP_base_url}api/system-config/default-pricing`;

      const requestData = {
        auth_code: process.env.REACT_APP_AUTH_CODE,
        masterAdminId: sessionStorage.getItem("TES_Admin_ID"),
        ...formData
      };

      const response = await axios.post(myurl, requestData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success === true) {
        toast.success("Pricing configuration updated successfully!");
        setActiveModal();
        setUpdateModal();
      } else {
        if (response.data.data && response.data.data.errors) {
          // Handle validation errors from server
          const serverErrors = {};
          response.data.data.errors.forEach(error => {
            // Try to match error message to field
            if (error.includes('baseCharge') || error.includes('Base charge')) {
              serverErrors.baseCharge = error;
            } else if (error.includes('companyUser')) {
              serverErrors.companyUser = error;
            } else if (error.includes('readWrite')) {
              serverErrors.readWrite = error;
            } else if (error.includes('topo')) {
              serverErrors.topo = error;
            } else if (error.includes('readOnly')) {
              serverErrors.readOnly = error;
            } else if (error.includes('foundationAnalysis')) {
              serverErrors.foundationAnalysisAddOn = error;
            } else if (error.includes('floorLevel')) {
              serverErrors.floorLevelAddOn = error;
            } else if (error.includes('currency') || error.includes('Currency')) {
              serverErrors.currency = error;
            }
          });
          setErrors(serverErrors);
          toast.error("Please fix the validation errors");
        } else {
          toast.error(response.data.message || "Failed to update pricing configuration");
        }
      }
    } catch (error) {
      console.error("Error updating pricing config:", error);
      if (error.response?.status >= 400 && error.response?.status < 500) {
        toast.error(error.response.data?.message || "Invalid request");
      } else {
        toast.error("Failed to update pricing configuration");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveModal();
    setErrors({});
  };

  const formatCurrency = (amount, currency) => {
    const currencyInfo = supportedCurrencies.find(c => c.code === currency);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // Thin wrapper over the shared FormGroup (also used by EditPaygPricingModal) - same
  // markup as before, it just binds this form's value / error / handlers.
  const FormGroup = (props) => (
    <SharedFormGroup
      {...props}
      value={formData[props.name]}
      error={errors[props.name]}
      onChange={handleInputChange}
      inputRef={setInputRef(props.name)}
    />
  );

  const calculateExampleCost = () => {
    return formData.baseCharge + formData.companyUser;
  };

  return (
    <Modal
      show={activeModal}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      centered
      className="pricing-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-dollar-sign me-2 mr-2"></i>
          Edit Default Pricing Configuration
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row">
            {/* Base Pricing Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-building form-section-icon"></i>
                  Base Pricing Configuration
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup
                      label="Base Monthly Charge"
                      name="baseCharge"
                      helpText="Fixed monthly charge applied to every company subscription"
                      icon="fa-home"
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="currency" className="form-label">
                        <i className="fa fa-globe me-2 mr-2"></i>
                        Currency
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.currency ? 'is-invalid' : ''}`}
                        id="currency"
                        name="currency"
                        value={formData.currency}
                        // onChange={handleInputChange}
                        required
                        disabled
                        defaultValue={"USD"}
                      >
                        {supportedCurrencies.map(curr => (
                          <option key={curr.code} value={curr.code}>
                            {curr.code} - {curr.name} ({curr.symbol})
                          </option>
                        ))}
                      </select>
                      {/* <div className="form-text">
                        Select the currency for all pricing calculations
                      </div> */}
                      {errors.currency && <div className="invalid-feedback">{errors.currency}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Type Pricing Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-users form-section-icon"></i>
                  User Type Pricing
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup
                      label="Company User"
                      name="companyUser"
                      helpText="Monthly cost per company admin user"
                      icon="fa-user-tie"
                    />
                  </div>
                  <div className="col-md-6">
                    <FormGroup
                      label="Read Write User"
                      name="readWrite"
                      helpText="Monthly cost per read-write access user"
                      icon="fa-edit"
                    />
                  </div>
                  <div className="col-md-6">
                    <FormGroup
                      label="Topo User (seat)"
                      name="topo"
                      helpText="Monthly cost per topology specialist user"
                      icon="fa-map"
                    />
                  </div>
                  <div className="col-md-6">
                    <FormGroup
                      label="Read Only User"
                      name="readOnly"
                      helpText="Monthly cost per read-only access user"
                      icon="fa-eye"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Add-On Features Section */}
            <div className="col-12">
              <div className="form-section">
                <h5 className="form-section-title">
                  <i className="fa fa-plus-circle form-section-icon"></i>
                  Add-On Features Pricing
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup
                      label="Foundation Analysis Add-On"
                      name="foundationAnalysisAddOn"
                      helpText="Monthly cost for foundation analysis feature"
                      icon="fa-hammer"
                    />
                  </div>
                  <div className="col-md-6">
                    <FormGroup
                      label="Floor Level Add-On"
                      name="floorLevelAddOn"
                      helpText="Monthly cost for floor level analysis feature"
                      icon="fa-layer-group"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Live Preview Section */}
            <div className="col-12">
              <div className="pricing-preview">
                <div className="preview-title">
                  <i className="fa fa-eye"></i>
                  Live Preview - Minimum Monthly Cost
                </div>
                <div className="preview-content">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="cost-breakdown">
                        <div className="cost-item">
                          <span className="cost-item-label">Base Charge:</span>
                          <span className="cost-item-value">
                            {formatCurrency(formData.baseCharge, formData.currency)}
                          </span>
                        </div>
                        <div className="cost-item">
                          <span className="cost-item-label">Company User (1x):</span>
                          <span className="cost-item-value">
                            {formatCurrency(formData.companyUser, formData.currency)}
                          </span>
                        </div>
                        <div className="cost-item">
                          <span className="cost-item-label">Total Monthly:</span>
                          <span className="cost-item-value cost-total">
                            {formatCurrency(calculateExampleCost(), formData.currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="alert alert-info mb-0">
                        <strong>Preview Note:</strong> This shows the minimum cost for a company with one admin user.
                        Additional users and add-on features will increase the total.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            <i className="fa fa-times me-2 mr-2"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fa fa-spinner fa-spin me-2 mr-2"></i>
                Saving Configuration...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2 mr-2"></i>
                Save Configuration
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditPricingModal;